import { cellToLatLng } from "h3-js";
import { mountPaperMap, type MapHandle, type MapOpts } from "./map-view.js";
import { DEFAULT_MAP_CENTER } from "../../map/tile-source.js";

type Mounter = (container: HTMLElement, opts: MapOpts) => MapHandle;

export interface NowMapResult {
  mount: HTMLElement;
  handle: MapHandle;
  /** True when this call (re)built the map (first build or the home cell changed). */
  rebuilt: boolean;
}

/** Owns the Now-tab PaperMap across re-renders.
 *
 *  The app re-renders the whole tab on every firehose event (app.ts onIncident →
 *  render()). Building a fresh WebGL map each render meant the map never lived long
 *  enough to fire 'load' and draw the home marker + radius during the cold-start
 *  incident backlog burst — so on reload the marker/radius were lost, and only a
 *  later stable mount (e.g. after setting home in You) showed them.
 *
 *  This caches one map per home cell: reused across renders, rebuilt only when the
 *  home cell changes (which also tears down the old map instead of leaking it). */
export class NowMapController {
  #mount: HTMLElement | null = null;
  #handle: MapHandle | null = null;
  #key: string | null = null;
  readonly #onMoveEnd?: (lat: number, lng: number) => void;
  readonly #onIncidentClick?: (subject: string) => void;
  readonly #onRawReportClick?: (id: string) => void;
  readonly #onSpreadClick?: (subject: string) => void;
  readonly #onFollowDisengaged?: () => void;
  readonly #mounter: Mounter;

  constructor(opts: {
    onMoveEnd?: (lat: number, lng: number) => void;
    onIncidentClick?: (subject: string) => void;
    onRawReportClick?: (id: string) => void;
    onSpreadClick?: (subject: string) => void;
    onFollowDisengaged?: () => void;
    mounter?: Mounter;
  } = {}) {
    this.#onMoveEnd = opts.onMoveEnd;
    this.#onIncidentClick = opts.onIncidentClick;
    this.#onRawReportClick = opts.onRawReportClick;
    this.#onSpreadClick = opts.onSpreadClick;
    this.#onFollowDisengaged = opts.onFollowDisengaged;
    this.#mounter = opts.mounter ?? mountPaperMap;
  }

  /** Get the persistent map mount + handle for the given home cell, rebuilding only
   *  when the cell changes (undefined = browse-only overview). */
  acquire(centerCell: string | undefined): NowMapResult {
    const key = centerCell ?? "";
    if (this.#mount && this.#handle && this.#key === key) {
      return { mount: this.#mount, handle: this.#handle, rebuilt: false };
    }
    this.#handle?.destroy();
    const mount = document.createElement("div");
    mount.className = "now-map";
    mount.setAttribute("data-map", "");
    const handle = this.#mounter(mount, {
      centerCell: centerCell || undefined,
      homeMarker: true,
      fitRadius: true,
      onMoveEnd: this.#onMoveEnd,
      onIncidentClick: this.#onIncidentClick,
      onRawReportClick: this.#onRawReportClick,
      onSpreadClick: this.#onSpreadClick,
      onFollowDisengaged: this.#onFollowDisengaged,
    });
    this.#mount = mount;
    this.#handle = handle;
    this.#key = key;
    // Seed the first viewport subscription from the new center (once per build, not
    // per render). Falls back to the Iberia overview for browse-only users.
    if (this.#onMoveEnd) {
      const [lat, lng] = centerCell ? cellToLatLng(centerCell) : [DEFAULT_MAP_CENTER[1], DEFAULT_MAP_CENTER[0]];
      this.#onMoveEnd(lat, lng);
    }
    return { mount, handle, rebuilt: true };
  }

  /** Tear down the persistent map (e.g. on erase/logout). */
  destroy(): void {
    this.#handle?.destroy();
    this.#mount = null;
    this.#handle = null;
    this.#key = null;
  }
}
